import type { MediaComment, MediaCommentsResponse } from '../../seeding-report/api/media-comments';
import { pickTopComments, type CommentFilter } from '../../seeding-report/lib/pick-comments';
import type { CollectRange } from './collect-account-stats';
import type { EnrichEntry, EnrichInput, EnrichOptions } from './enrich-report-rows';
import type { TopVideoRow, TopVideosSection } from './export-top-videos-docx';
import type { AccountStatRow } from './stat-row';
import { cleanPermalink, metricsFromStat, pickTopVideos, type TopVideoPlatform } from './top-videos';

/**
 * Сборка отчёта «Топ видео» по нескольким аккаунтам.
 *
 * Сеть подаётся снаружи (deps), чтобы порядок и обработку сбоев можно было
 * проверить тестом — это и есть самое хрупкое место: отчёт по десяти
 * аккаунтам не должен пропадать целиком из-за одного отозванного токена.
 */

export type TopVideosAccount = {
  id: string;
  /** Как аккаунт подписан в отчёте: «@username». */
  title: string;
  username?: string;
  platform: TopVideoPlatform;
};

export type TopVideosDeps = {
  collect: (account: TopVideosAccount, range: CollectRange) => Promise<AccountStatRow[]>;
  enrich: (rows: EnrichInput[], options: EnrichOptions) => Promise<Map<string, EnrichEntry>>;
  captionHeadline: (username: string | undefined) => boolean;
  /**
   * «Топовые комментарии» под роликами; не задано — блока не будет. Ходим
   * только за роликами топа, у которых счётчик комментариев не ноль.
   */
  comments?: {
    /** Сколько комментариев оставить под роликом. */
    count: number;
    fetch: (account: TopVideosAccount, mediaIds: string[]) => Promise<MediaCommentsResponse>;
    /** Отсев негатива (seeding-report/lib/screen-comments). */
    filter?: CommentFilter;
  };
};

export type TopVideosBuildResult = {
  sections: TopVideosSection[];
  /** Аккаунты, сбор по которым упал: в отчёт не попали. */
  failed: { title: string; message: string }[];
  /** Аккаунты без единого ролика за период. */
  empty: string[];
  /** Ролики, комментарии к которым не прочитались: блок у них пустой. */
  commentErrors: { title: string; mediaId: string; error: string }[];
};

export async function buildTopVideosReport(
  accounts: TopVideosAccount[],
  range: CollectRange,
  topCount: number,
  deps: TopVideosDeps,
  onProgress: (message: string) => void = () => {},
): Promise<TopVideosBuildResult> {
  const result: TopVideosBuildResult = { sections: [], failed: [], empty: [], commentErrors: [] };

  // По аккаунтам — строго по очереди. Параллельно было бы быстрее, но каждый
  // сбор сам по себе гонит пачку запросов к Instagram, и десять таких разом
  // упираются в лимиты Graph API.
  for (const [i, account] of accounts.entries()) {
    const step = `${account.title} (${i + 1}/${accounts.length})`;
    try {
      onProgress(`${step}: сбор публикаций...`);
      const rows = await deps.collect(account, range);
      if (rows.length === 0) {
        result.empty.push(account.title);
        continue;
      }

      // Темы и кадры — только для попавших в топ: GPT платный, а из сотни
      // роликов в отчёт уходит пять.
      const top = pickTopVideos(rows, topCount);
      onProgress(`${step}: темы и кадры...`);
      const enriched = await deps.enrich(
        top.map((r) => ({
          mediaId: String(r.mediaId),
          thumbnailUrl: r.thumbnailUrl || undefined,
          caption: r.caption,
        })),
        { useCaptionHeadline: deps.captionHeadline(account.username) },
      );

      // Комментарии — после тем: сбой здесь не должен стоить аккаунту отчёта.
      const byMedia = new Map<string, MediaComment[]>();
      const wanted = top.filter((r) => r.comments > 0).map((r) => String(r.mediaId));
      if (deps.comments && deps.comments.count > 0 && wanted.length > 0) {
        onProgress(`${step}: комментарии...`);
        try {
          const res = await deps.comments.fetch(account, wanted);
          for (const c of res.comments) byMedia.set(c.mediaId, [...(byMedia.get(c.mediaId) ?? []), c]);
          for (const e of res.errors) result.commentErrors.push({ title: account.title, ...e });
        } catch (err) {
          const error = err instanceof Error ? err.message : String(err);
          for (const mediaId of wanted) result.commentErrors.push({ title: account.title, mediaId, error });
        }
      }
      const picked = new Map<string, TopVideoRow['comments']>();
      if (deps.comments && byMedia.size > 0) {
        const { count, filter } = deps.comments;
        for (const [mediaId, list] of byMedia) picked.set(mediaId, await pickTopComments(list, { count, filter }));
      }

      const videos: TopVideoRow[] = top.map((r) => {
        const e = enriched.get(String(r.mediaId));
        return {
          permalink: cleanPermalink(r.permalink ?? ''),
          theme: e?.theme ?? '',
          imageUrl: e?.imageUrl || r.thumbnailUrl || '',
          metrics: metricsFromStat(r, account.platform),
          ...(picked.has(String(r.mediaId)) ? { comments: picked.get(String(r.mediaId)) } : {}),
        };
      });

      result.sections.push({ accountTitle: account.title, platform: account.platform, rows: videos });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[Top Videos] ${account.title} — сбой:`, err);
      result.failed.push({ title: account.title, message });
    }
  }

  return result;
}
